import { SubmissionStatus } from '@bp/bp-graphql-types';
import { ArrowHeadDownIcon, ArrowHeadLeftIcon, CheckIcon, Grid, GridColumn, GridRow } from '@bp/ui-components';
import { ActivityProtocol } from 'components/ActivityProtocol/ActivityProtocol';
import { BpActionButton, BpCard } from 'components/BpCard/BpCard';
import { BpHeader } from 'components/BpHeader/BpHeader';
import { BpSubpage } from 'components/BpSubpage/BpSubpage';
import { SubmissionAnswerCardTeacher } from 'components/Submissions/components/SubmissionAnswerCard/SubmissionAnswerCardTeacher';
import { SubmissionFeedbackCard } from 'components/Submissions/components/SubmissionFeedbackCard/SubmissionFeedbackCard';
import { UpdateSubmissionsMutation, useSubmissions } from 'components/Submissions/hooks/useSubmissions';
import { SubmissionFeedbackModal } from 'components/Submissions/modals/SubmissionFeedbackModal';
import { SubmissionPublicationRequestModal } from 'components/Submissions/modals/SubmissionPublicationRequestModal';
import { getSubmissionStatusTranslation } from 'components/Submissions/utils/submissionUtils';
import { useActivityProtocol } from 'hooks/matrix/useActivityProtocol';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useRequireParams } from 'hooks/useRequireParams';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'urql';
import { NotificationReason } from 'utils/matrixClient';
import { lowercaseFirstLetter } from 'utils/stringHelper';
import { showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import { useMessageCommands } from '../../../../matrix/commands/useMessageCommands';

type CourseSubmissionTeacherContentProps = {
  submissionUuid: string;
};

export const CourseSubmissionTeacherContent = ({ submissionUuid }: CourseSubmissionTeacherContentProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const { courseUuid, assignmentUuid } = useRequireParams(['courseUuid', 'assignmentUuid']);
  const navigate = useNavigate();

  const { sendMessage } = useActivityProtocol();
  const { markAsRead } = useMessageCommands();

  const [, updateSubmission] = useMutation(UpdateSubmissionsMutation);

  const [loading, setLoading] = useState<boolean>(false);
  const [publicationModalOpen, setPublicationModalOpen] = useState<boolean>(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState<boolean>(false);

  const { submissions } = useSubmissions({ courseUuid, assignmentUuid });

  const submission = submissions.find((s) => s.uuid === submissionUuid);
  if (!submission) throw new Error('Submission not found');

  const otherSubmissions = submissions.filter((s) => s.status === submission.status);
  const submissionIndex = otherSubmissions.findIndex((s) => s.uuid === submissionUuid) ?? 0;

  const permissions = usePermissionChecker();

  const canViewSubmissions = permissions.canViewSubmissions({
    uuid: submission.uuid,
    ownerUuid: submission.owner.uuid,
    groupUuid: submission.assignment.course.uuid,
    organization: pimAuthClaims.getOrganization(),
  });

  async function handleUpdateSubmission(uuid: string, status: SubmissionStatus): Promise<boolean> {
    setLoading(true);
    const { error } = await updateSubmission({ where: { uuid }, update: { status } });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessUpdateToast();
    }
    setLoading(false);
    return !error;
  }

  function handleBack() {
    navigate(`/courses/${courseUuid}/assignments/${assignmentUuid}`);
  }

  function handleNext(index: number) {
    const nextIndex = index + 1;
    if (otherSubmissions.length === nextIndex) {
      // last Submission -> back to overview
      handleBack();
    } else {
      const nextUuid = otherSubmissions[nextIndex]?.uuid;
      navigate(`/courses/${courseUuid}/assignments/${assignmentUuid}/submissions/${nextUuid}`);
    }
  }

  async function handleDoneAndNext(index: number) {
    const didSucceed = await handleUpdateSubmission(submissionUuid, SubmissionStatus.Done);
    if (didSucceed) {
      handleNext(index);
    }
  }

  async function handleReopen() {
    const didSucceed = await handleUpdateSubmission(submissionUuid, SubmissionStatus.Feedback);
    if (didSucceed) {
      await sendMessage(submissionUuid, NotificationReason.SubmissionFeedback);
    }
  }

  function handleRequestPublication() {
    setPublicationModalOpen(true);
  }

  function handleFeedback() {
    setFeedbackModalOpen(true);
  }

  function handleClose() {
    setPublicationModalOpen(false);
    setFeedbackModalOpen(false);
  }

  useEffect(() => {
    if (submission.newMessage) {
      markAsRead(submission.room?.roomId);
    }
  }, [markAsRead, submission.newMessage, submission.room?.roomId]);

  return (
    <BpSubpage isForbidden={!canViewSubmissions} loading={loading}>
      <BpHeader
        headline={submission.owner.name}
        subHeadline={`${submission.assignment.title} - ${getSubmissionStatusTranslation(submission.status, 'teacher')} - ${submissionIndex + 1} ${lowercaseFirstLetter(t('common.from'))} ${otherSubmissions.length}`}
        showSubHeadlineAsChip
        actions={[
          {
            text: t('common.back'),
            hierarchy: 'secondary',
            icon: <ArrowHeadLeftIcon />,
            callback: () => handleBack(),
          },
          ...(submission.status === SubmissionStatus.New
            ? [
                {
                  text: t('submissions.finishAndNext'),
                  hierarchy: 'secondary',
                  icon: <CheckIcon />,
                  callback: () => handleDoneAndNext(submissionIndex),
                } as BpActionButton,
              ]
            : []),
          ...(otherSubmissions.length !== submissionIndex + 1
            ? [
                {
                  text: t('common.next'),
                  hierarchy: 'secondary',
                  icon: <ArrowHeadDownIcon />,
                  callback: () => handleNext(submissionIndex),
                } as BpActionButton,
              ]
            : []),
        ]}
      />

      <Grid>
        <GridRow>
          <GridColumn width={8}>
            <SubmissionAnswerCardTeacher
              submissionUuid={submissionUuid}
              onRequestPublication={() => handleRequestPublication()}
              onReopen={() => handleReopen()}
            />
            {(submission.status === SubmissionStatus.New ||
              submission.status === SubmissionStatus.Updated ||
              submission.status === SubmissionStatus.Feedback) && (
              <SubmissionFeedbackCard
                submissionUuid={submissionUuid}
                onFeedback={() => handleFeedback()}
                className='mt-6'
              />
            )}
          </GridColumn>
          <GridColumn width={4}>
            <BpCard
              alwaysCollapsible
              header={{
                headline: t('activityProtocol.title'),
              }}
            >
              <ActivityProtocol submissionUuid={submissionUuid} />
            </BpCard>
          </GridColumn>
        </GridRow>
      </Grid>

      <SubmissionPublicationRequestModal
        submissionUuid={submissionUuid}
        name={submission.owner.name}
        isOpen={publicationModalOpen}
        onClose={() => handleClose()}
      />

      <SubmissionFeedbackModal
        submissionUuid={submissionUuid}
        isOpen={feedbackModalOpen}
        onClose={() => handleClose()}
      />
    </BpSubpage>
  );
};
